import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { AuthService } from '../../services/auth.service';
import { IdleSessionService } from '../../services/idle-session.service';

const MAX_ATTEMPTS = 3;

/**
 * Verrouillage de session. Monte une seule fois dans le layout, et porte deux etats.
 *
 * 1. **Avertissement** — dans la derniere minute d'inactivite, un bandeau annonce le
 *    verrouillage a venir avec un compte a rebours. L'ecran ne se floute plus sans preavis :
 *    un agent en train de lire un long tableau garde la main d'un clic.
 * 2. **Verrouille** — quand `IdleSessionService.locked()` est vrai : floute l'arriere-plan
 *    (confidentialite d'ecran) et exige la ressaisie du mot de passe (ré-auth reelle
 *    via /auth/token/). Apres 3 echecs → deconnexion.
 */
@Component({
    selector: 'app-lock-overlay',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [FormsModule, MatButtonModule, MatFormFieldModule, MatInputModule, MatIconModule, ButtonComponent],
    templateUrl: './lock-overlay.component.html',
})
export class LockOverlayComponent {
    private readonly authService = inject(AuthService);
    readonly idle = inject(IdleSessionService);
    private readonly destroyRef = inject(DestroyRef);

    readonly user = this.authService.user;
    readonly password = signal('');
    readonly showPassword = signal(false);
    readonly loading = signal(false);
    readonly error = signal('');
    readonly attempts = signal(0);
    readonly remainingAttempts = computed(() => Math.max(0, MAX_ATTEMPTS - this.attempts()));

    /** Rearme le minuteur depuis l'avertissement : aucune ressaisie, la session n'a jamais ete verrouillee. */
    resterConnecte(): void {
        this.idle.resterConnecte();
    }

    submit(): void {
        const pwd = this.password().trim();
        if (!pwd || this.loading()) {
            return;
        }
        this.loading.set(true);
        this.error.set('');
        this.authService
            .reauthenticate(pwd)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (ok) => {
                    this.loading.set(false);
                    if (ok) {
                        this.reset();
                        this.idle.unlock();
                    } else {
                        this.onFailure();
                    }
                },
                error: () => {
                    this.loading.set(false);
                    this.onFailure();
                },
            });
    }

    logout(): void {
        this.reset();
        this.idle.forceLogout();
    }

    private onFailure(): void {
        this.password.set('');
        this.attempts.update((a) => a + 1);
        if (this.attempts() >= MAX_ATTEMPTS) {
            this.error.set('Trop de tentatives. Déconnexion…');
            this.logout();

            return;
        }
        this.error.set(`Mot de passe incorrect. ${this.remainingAttempts()} tentative(s) restante(s).`);
    }

    private reset(): void {
        this.password.set('');
        this.error.set('');
        this.attempts.set(0);
        this.loading.set(false);
        this.showPassword.set(false);
    }
}
